// Translation utilities
import { translationApi } from "@/services/translationApi";

/**
 * Minimal fallback translations for when API is unavailable
 * Keep only essential UI elements since app requires backend API
 */
export const fallbackTranslations = {
  en: {
    common: {
      loading: "Loading...",
      error: "Error",
      ok: "OK",
      cancel: "Cancel",
      close: "Close",
    },
  },
  mk: {
    common: {
      loading: "Се вчитува...",
      error: "Грешка",
      ok: "ОК",
      cancel: "Откажи",
      close: "Затвори",
    },
  },
};

const supportedLanguages = ["en", "mk", "sq", "tr"];

export const resolveTranslationValue = (source, keyPath) => {
  if (!source) {
    return undefined;
  }

  const segments = Array.isArray(keyPath) ? keyPath : keyPath.split(".");
  let current = source;

  for (let index = 0; index < segments.length; index += 1) {
    const segment = segments[index];

    if (current && typeof current === "object" && segment in current) {
      current = current[segment];
      continue;
    }

    if (current && typeof current === "object") {
      const remainingKey = segments.slice(index).join(".");
      if (
        remainingKey &&
        Object.prototype.hasOwnProperty.call(current, remainingKey)
      ) {
        return current[remainingKey];
      }
    }

    return undefined;
  }

  // If we resolved to a non-string value, check if the entire keyPath
  // exists as a non-nested (direct) key in the source object
  if (typeof current !== "string") {
    const flatKey = Array.isArray(keyPath) ? keyPath.join(".") : keyPath;
    if (
      Object.prototype.hasOwnProperty.call(source, flatKey) &&
      typeof source[flatKey] === "string"
    ) {
      return source[flatKey];
    }
  }

  return current;
};

/**
 * Get fallback translations for a specific language
 * @param {string} language - Language code
 * @returns {Object} Fallback translations object
 */
export const getFallbackTranslations = (language) => {
  return fallbackTranslations[language] || fallbackTranslations.en;
};

/**
 * Merge server translations with fallback translations
 * @param {Object} serverTranslations - Translations from server
 * @param {string} language - Language code
 * @returns {Object} Merged translations
 */
export const mergeWithFallback = (serverTranslations, language) => {
  const fallback = getFallbackTranslations(language);
  return deepMerge(fallback, serverTranslations || {});
};

/**
 * Deep merge two objects
 * @param {Object} target - Target object
 * @param {Object} source - Source object
 * @returns {Object} Merged object
 */
export const deepMerge = (target, source) => {
  const result = { ...target };

  for (const key in source) {
    if (
      source[key] &&
      typeof source[key] === "object" &&
      !Array.isArray(source[key])
    ) {
      result[key] = deepMerge(result[key] || {}, source[key]);
    } else {
      result[key] = source[key];
    }
  }

  return result;
};

/**
 * Preload all translations with fallback support
 * @param {string[]} languages - Languages to preload
 * @returns {Promise<Object>} Preloaded translations
 */
export const preloadAllTranslations = async (
  languages = supportedLanguages
) => {
  const results = {};

  for (const language of languages) {
    try {
      const serverTranslations = await translationApi.loadTranslations(
        language
      );
      results[language] = mergeWithFallback(serverTranslations, language);
    } catch (error) {
      console.warn(
        `Failed to load translations for ${language}, using fallback:`,
        error
      );
      results[language] = getFallbackTranslations(language);
    }
  }

  return results;
};

/**
 * Initialize translation system with error handling
 * @param {Function} setTranslations - Function to set translations in store
 * @param {Function} setCurrentLanguage - Function to set current language
 * @returns {Promise<void>}
 */
export const initializeTranslationSystem = async (
  setTranslations,
  setCurrentLanguage
) => {
  try {
    // Get preferred language from localStorage
    let preferredLang = "en";
    if (typeof window !== "undefined") {
      preferredLang = localStorage.getItem("preferred_language") || "en";
    }

    // Try to load translations for preferred language
    const translations = await preloadAllTranslations(supportedLanguages);

    // Set translations in store
    setTranslations(translations);
    setCurrentLanguage(preferredLang);

    console.log("Translation system initialized successfully");
  } catch (error) {
    console.error("Failed to initialize translation system:", error);

    // Use fallback translations
    const fallbackTranslations = {
      en: getFallbackTranslations("en"),
      mk: getFallbackTranslations("mk"),
    };

    setTranslations(fallbackTranslations);
    setCurrentLanguage("en");
  }
};
